import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { startOfMonth, subMonths } from 'date-fns';
import { AnyAction } from 'redux';

import {
  AppState,
  reportFilterStartBusinessDateSelector,
  reportFilterEndBusinessDateSelector,
  changeFilterStartBusinessDate,
  changeFilterEndBusinessDate,
  filterParametersDataSelector,
} from '../../../state';
import { constants, isEqualNullableDate } from '../../../helpers';
import DateRangeFlexSales, {
  DateRangeFlexSalesProps,
  DateRangeFlexSalesValue,
} from '../../DateRangeFlexSales';

type DateSelectorValue = {
  raw: object | null | undefined;
  accept: Date | null | undefined;
};

type BusinessDateRangeFilterProps = Pick<DateRangeFlexSalesProps<Date>, 'spacing'> & {
  maxDaysBetween?: number; // Nueva prop opcional
  requiredIncomplete?: boolean;
  maxDate?: Date;
  startTextKey?: string;
  endTextKey?: string;
  startDateSelector?: (state: AppState) => DateSelectorValue;
  endDateSelector?: (state: AppState) => DateSelectorValue;
  changeStartDateAction?: (value: Date | null) => AnyAction;
  changeEndDateAction?: (value: Date | null) => AnyAction;
};

const BusinessDateRangeFilter = ({
  maxDate,
  startTextKey = 'toolbar.labels.fromDateBusiness',
  endTextKey = 'toolbar.labels.toDateBusiness',
  startDateSelector = reportFilterStartBusinessDateSelector,
  endDateSelector = reportFilterEndBusinessDateSelector,
  changeStartDateAction = changeFilterStartBusinessDate,
  changeEndDateAction = changeFilterEndBusinessDate,
  ...props
}: BusinessDateRangeFilterProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const startDate = useSelector(startDateSelector);
  const endDate = useSelector(endDateSelector);
  const filterParameters = useSelector(filterParametersDataSelector);
  const minDate = startOfMonth(subMonths(new Date(), filterParameters?.filtersLimitMonths || 3));

  const dateValue: DateRangeFlexSalesValue<Date> = useMemo(
    () => [startDate.raw, endDate.raw] as DateRangeFlexSalesValue<Date>,
    [startDate.raw, endDate.raw]
  );

  const handleChange = useCallback(
    ([newStartDate, newEndDate]: DateRangeFlexSalesValue<Date>) => {
      if (!isEqualNullableDate(newStartDate, startDate.raw as Date)) {
        dispatch(changeStartDateAction(newStartDate));
      } else if (!isEqualNullableDate(newEndDate, endDate.raw as Date))
        dispatch(changeEndDateAction(newEndDate));
    },
    [dispatch, endDate, startDate, changeStartDateAction, changeEndDateAction]
  );

  return (
    <DateRangeFlexSales
      {...props}
      maxDate={maxDate || constants.reportFilterRangeMaxDate}
      minDate={minDate}
      startText={t(startTextKey)}
      endText={t(endTextKey)}
      value={dateValue}
      onChange={handleChange}
    />
  );
};

export default BusinessDateRangeFilter;
